import type { CSSObject } from '@emotion/react'
import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import { inScrollingDesktop } from '../../style/scrollingLayout'
import {
    imageLqipPreviewUrl,
    supportsImageLqipPreview,
} from '../../utils/imageUrlTransform'

/**
 * A photograph fills its frame; a cutout is a subject on empty margin. Nothing
 * in the URL says which has arrived, and the two want opposite treatment.
 */
export type ImageKind = 'photo' | 'cutout'

/** Null until the preview has been read, and for a host that serves none. */
export type MaybeImageKind = ImageKind | null

/** Anything short of this is transparent enough to shape a shadow around. */
const OPAQUE_ALPHA = 250

/** Enough of the image to find transparency in; cheaper than a full decode. */
const PROBE_PX = 32

/** A lone image is shown at this width at most, whatever its proportions. */
export const SINGLE_IMAGE_MAX_WIDTH = 500

/**
 * The shadow is visual weight below the image, so matching the space above it
 * reads as the whole band sliding downward.
 */
const BAND_PADDING_TOP_PX = 12
const BAND_PADDING_BOTTOM_PX = 24

/**
 * A shadow reaches about its vertical offset plus its blur, and the band clips
 * at the padding above, so both of these stay well inside it rather than
 * ending on a cut edge.
 */
const PHOTO_SHADOW = { offsetY: 6, blur: 12, spread: -2 }
const CUTOUT_SHADOW = { offsetY: 5, blur: 8 }

const hasTransparency = (image: HTMLImageElement): boolean => {
    const canvas = document.createElement('canvas')
    canvas.width = PROBE_PX
    canvas.height = PROBE_PX
    const context = canvas.getContext('2d')
    if (!context) return false

    context.drawImage(image, 0, 0, PROBE_PX, PROBE_PX)
    const { data } = context.getImageData(0, 0, PROBE_PX, PROBE_PX)
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < OPAQUE_ALPHA) return true
    }
    return false
}

/**
 * Reads the preview rendition the backdrop already fetches, so classifying an
 * image costs a small canvas pass and no second download. Anything that taints
 * the canvas falls back to the photograph treatment, which is what every
 * opaque image wants anyway.
 */
export const useImageKind = (imageUrl: string | null): MaybeImageKind => {
    const [probed, setProbed] = useState<{ url: string; kind: ImageKind }>()
    const previewUrl =
        imageUrl && supportsImageLqipPreview(imageUrl)
            ? imageLqipPreviewUrl(imageUrl)
            : null

    useEffect(() => {
        if (!previewUrl) return

        let cancelled = false
        const probe = new window.Image()
        // Without this the canvas is tainted and reading it back throws.
        probe.crossOrigin = 'anonymous'
        probe.onload = () => {
            if (cancelled) return
            let kind: ImageKind = 'photo'
            try {
                kind = hasTransparency(probe) ? 'cutout' : 'photo'
            } catch {
                kind = 'photo'
            }
            setProbed({ url: previewUrl, kind })
        }
        probe.src = previewUrl

        return () => {
            cancelled = true
        }
    }, [previewUrl])

    return probed?.url === previewUrl ? probed.kind : null
}

/**
 * Clearance, width and shadow for a band holding one image. Returned unwrapped
 * so a caller can compose it into its own container query rather than
 * colliding with it on the same key.
 */
export const singleImageBandStyles = (kind: MaybeImageKind): CSSObject => ({
    paddingTop: BAND_PADDING_TOP_PX,
    paddingBottom: BAND_PADDING_BOTTOM_PX,
    img: {
        maxWidth: SINGLE_IMAGE_MAX_WIDTH,
        // The page is far wider than the image now, so it would otherwise sit
        // against the start edge of a band that spans the whole page.
        marginInline: 'auto',
        // `box-shadow` follows the border box and is the cheaper of the two,
        // but behind a cutout it draws a rectangle the subject does not fill.
        // `drop-shadow` reads the alpha mask and casts the subject's own shape.
        // Neither until the kind is known.
        ...(kind === 'cutout' && {
            filter: `drop-shadow(0 ${CUTOUT_SHADOW.offsetY}px ${CUTOUT_SHADOW.blur}px rgba(0, 0, 0, 0.45))`,
        }),
        ...(kind === 'photo' && {
            boxShadow: `0 ${PHOTO_SHADOW.offsetY}px ${PHOTO_SHADOW.blur}px ${PHOTO_SHADOW.spread}px rgba(0, 0, 0, 0.5)`,
        }),
    },
})

/**
 * Hidden until the scrolling layout runs wide, which is the only place an image
 * is given more width than it fills. `display: none` also keeps the browser
 * from fetching the preview everywhere else.
 */
const Backdrop = styled.div({
    display: 'none',
    ...inScrollingDesktop({
        display: 'block',
        position: 'absolute',
        inset: 0,
        zIndex: -1,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        // Scaled up so the blur's soft edge falls outside the frame instead of
        // fading to the page colour just inside it.
        filter: 'blur(48px) saturate(1.4)',
        transform: 'scale(1.2)',
        pointerEvents: 'none',
    }),
})

/**
 * The image's own colours behind it, for a frame it does not fill. Built from
 * the preview rendition the galleries already use as a placeholder, so this
 * costs a blurred CSS layer rather than a second full-size download.
 *
 * A cutout gets nothing: there is no background in the file to carry across
 * the band, only transparent margin, so blurring it smears the subject wide
 * instead. The page's own colour is the backdrop there.
 *
 * Needs a positioned ancestor that clips, and one that isolates if anything
 * else in the frame paints below the content.
 */
export const ImageBackdrop: React.FC<{
    imageUrl: string
    kind: MaybeImageKind
}> = ({ imageUrl, kind }) => {
    if (kind === 'cutout') return null
    if (!supportsImageLqipPreview(imageUrl)) return null

    return (
        <Backdrop
            aria-hidden
            style={{
                backgroundImage: `url(${imageLqipPreviewUrl(imageUrl)})`,
            }}
        />
    )
}
